import type { Damage, Poison, SpellDef, SpellEffect, Stats } from '../../data/schema';
import { hitTarget, playerDamagePct, scaleDamage } from '../combat';
import { recomputeStats } from '../equipment';
import { AIM_ASSIST_CONE_DEG, AIM_ASSIST_RANGE, AUTO_AIM_RANGE } from '../config';
import { effectiveCooldown } from '../progression';
import { STAT_FOR_ELEMENT, type Hazard, type InputFrame, type Monster, type OwnedSpell, type Player, type Projectile, type Vec2 } from '../types';
import { bodyHeight, clampToWalkable, entitiesOfKind, getPlayer, spawn, type World } from '../world';
import { spawnSummon } from './summons';

export function aimDir(from: Vec2, to: Vec2 | null, fallback: Vec2): Vec2 {
  if (!to) return { ...fallback };
  const dx = to.x - from.x, dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  return len < 1 ? { ...fallback } : { x: dx / len, y: dy / len };
}
/**
 * Dégâts affichés d'un sort possédé : premier effet à dégâts (projectile, zone ou mêlée), mods et bonus de dégâts du joueur (`bonusPct`) appliqués,
 * puis bonus de la stat élémentaire (même formule que rollDamage, sans résistance), puis bonus de dégâts de méta (`metaDamagePct`, multiplicatif comme en combat).
 */
export function effectiveDamage(spell: SpellDef, owned: OwnedSpell, stats: Stats, bonusPct = 0, metaDamagePct = 0): Damage | null {
  const fx = spell.effects.find((e) => e.type === 'projectile' || e.type === 'zone' || e.type === 'melee');
  if (!fx || (fx.type !== 'projectile' && fx.type !== 'zone' && fx.type !== 'melee')) return null;
  const d = scaleDamage(fx.damage, owned.mods.damagePct + bonusPct);
  const statKey = STAT_FOR_ELEMENT[d.element];
  const stat = statKey ? stats[statKey] : 0;
  const mult = 1 + metaDamagePct / 100;
  return { element: d.element, min: Math.floor(d.min * (100 + stat) / 100 * mult), max: Math.floor(d.max * (100 + stat) / 100 * mult) };
}
const rotate = (v: Vec2, deg: number): Vec2 => { const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a); return { x: v.x * c - v.y * s, y: v.x * s + v.y * c }; };

/** Centre du corps d'un monstre (mi-hauteur au-dessus des pieds) : la cible visuelle du joueur. */
function bodyCenter(m: Monster): Vec2 { return { x: m.pos.x, y: m.pos.y - bodyHeight(m) / 2 }; }

const CONE_COS = Math.cos(AIM_ASSIST_CONE_DEG * Math.PI / 180);
const TIE_EPS = 1e-9;

/** Visée résolue : direction de tir et, sans pointeur, le monstre visé automatiquement (centre des zones « à la visée »). */
export interface ResolvedAim { dir: Vec2; target: Monster | null; }

/**
 * Direction de tir. Souris : direction brute, recalée sur le centre du corps du monstre vivant le plus proche
 * angulairement dans un cône de AIM_ASSIST_CONE_DEG (portée AIM_ASSIST_RANGE) ; à angle égal, le plus proche gagne.
 * Sans pointeur (aim = null) : monstre vivant le plus proche à moins de AUTO_AIM_RANGE, sinon direction regardée.
 * Les angles sont comparés par leur cosinus (produit scalaire de vecteurs unitaires), sans fonction transcendante.
 */
export function resolveAim(world: World, p: Player, aim: Vec2 | null): ResolvedAim {
  const monsters = entitiesOfKind<Monster>(world, 'monster').filter((m) => m.alive);
  if (aim === null) {
    let best: Monster | null = null;
    let bestDist = Infinity;
    for (const m of monsters) {
      const c = bodyCenter(m);
      const d = Math.hypot(c.x - p.pos.x, c.y - p.pos.y);
      if (d < 1 || d > AUTO_AIM_RANGE) continue;
      if (best !== null && d >= bestDist) continue;
      bestDist = d;
      best = m;
    }
    return best ? { dir: aimDir(p.pos, bodyCenter(best), p.facing), target: best } : { dir: { ...p.facing }, target: null };
  }
  const raw = aimDir(p.pos, aim, p.facing);
  let bestDir: Vec2 | null = null;
  let bestDot = CONE_COS;
  let bestDist = Infinity;
  for (const m of monsters) {
    const c = bodyCenter(m);
    const dist = Math.hypot(c.x - p.pos.x, c.y - p.pos.y);
    if (dist < 1 || dist > AIM_ASSIST_RANGE) continue;
    const dir = { x: (c.x - p.pos.x) / dist, y: (c.y - p.pos.y) / dist };
    const dot = raw.x * dir.x + raw.y * dir.y;
    if (dot < CONE_COS) continue;
    if (bestDir !== null) {
      if (dot < bestDot - TIE_EPS) continue;
      if (dot <= bestDot + TIE_EPS && dist >= bestDist) continue;
    }
    bestDot = dot;
    bestDist = dist;
    bestDir = dir;
  }
  return { dir: bestDir ?? raw, target: null };
}

export function castSpell(world: World, p: Player, owned: OwnedSpell, aim: Vec2 | null): void {
  const spell = world.content.spells[owned.id];
  if (!spell) return;
  const resolved = resolveAim(world, p, aim);
  const dir = resolved.dir;
  p.facing = dir;
  p.anim = 'attack'; p.animTime = 0;
  for (const fx of spell.effects) applyEffect(world, p, owned, fx, dir, aim, resolved.target);
  world.events.push({ type: 'spellCast', spellId: spell.id, pos: { ...p.pos }, dir });
}

/** Centre d'un effet « à la visée » ou « sur soi » : sans pointeur, le monstre visé automatiquement (pieds), sinon la portée maximale devant soi ; borné à maxRange. */
function effectCenter(p: Player, at: 'aim' | 'self', maxRange: number, dir: Vec2, aim: Vec2 | null, autoTarget: Monster | null): Vec2 {
  if (at === 'self') return { ...p.pos };
  const target = aim ?? (autoTarget ? { ...autoTarget.pos } : { x: p.pos.x + dir.x * maxRange, y: p.pos.y + dir.y * maxRange });
  const dx = target.x - p.pos.x, dy = target.y - p.pos.y, len = Math.hypot(dx, dy);
  return len > maxRange ? { x: p.pos.x + (dx / len) * maxRange, y: p.pos.y + (dy / len) * maxRange } : { ...target };
}

function applyEffect(world: World, p: Player, owned: OwnedSpell, fx: SpellEffect, dir: Vec2, aim: Vec2 | null, autoTarget: Monster | null): void {
  const m = owned.mods;
  if (fx.type === 'projectile') {
    const n = fx.count + m.countBonus;
    const spread = n > 1 ? Math.max(fx.spreadDeg, 10 * (n - 1)) : 0;
    for (let i = 0; i < n; i++) {
      const d = n > 1 ? rotate(dir, -spread / 2 + (spread * i) / (n - 1)) : dir;
      spawn<Projectile>(world, { kind: 'projectile', team: 'player', defId: owned.id, sprite: owned.id, pos: { x: p.pos.x + d.x * p.radius, y: p.pos.y + d.y * p.radius }, vel: { x: d.x * fx.speed, y: d.y * fx.speed }, radius: fx.radius, facing: d, hp: 1, hpMax: 1, alive: true, anim: 'idle', animTime: 0, damage: scaleDamage(fx.damage, m.damagePct + playerDamagePct(world, p)), lifetime: fx.lifetime, pierce: fx.pierce, hitIds: new Set(), ownerId: p.id, slow: fx.slow ?? null, lifestealPct: fx.lifestealPct, poison: fx.poison ?? latentPoison(world, p, owned) });
    }
  } else if (fx.type === 'zone') {
    const center = effectCenter(p, fx.at, fx.maxRange, dir, aim, autoTarget);
    const radius = fx.radius * (1 + m.radiusPct / 100);
    const dmg = scaleDamage(fx.damage, m.damagePct + playerDamagePct(world, p));
    for (const mon of entitiesOfKind<Monster>(world, 'monster')) {
      if (!mon.alive || Math.hypot(mon.pos.x - center.x, mon.pos.y - center.y) > radius + mon.radius) continue;
      hitTarget(world, p, mon, dmg, { lifestealPct: fx.lifestealPct, slow: fx.slow ?? null, poison: fx.poison ?? null }, mon.pos);
    }
    world.events.push({ type: 'zoneHit', pos: center, radius, element: dmg.element });
  } else if (fx.type === 'blink') {
    // la téléportation suit la visée brute (ni aim assist ni visée automatique) : sans pointeur, la direction regardée
    const bdir = aimDir(p.pos, aim, p.facing);
    const from = { ...p.pos };
    p.pos = clampToWalkable({ x: p.pos.x + bdir.x * fx.distance, y: p.pos.y + bdir.y * fx.distance }, p.radius, world.room);
    world.events.push({ type: 'blink', from, to: { ...p.pos } });
  } else if (fx.type === 'shield') {
    p.shield = { amount: fx.amount, until: world.time + fx.duration };
  } else if (fx.type === 'melee') {
    applyMelee(world, p, owned, fx, dir);
  } else if (fx.type === 'buff') {
    applyBuff(world, p, owned, fx);
  } else if (fx.type === 'pull') {
    applyPull(world, p, owned, fx, effectCenter(p, fx.at, fx.maxRange, dir, aim, autoTarget));
  } else if (fx.type === 'swap') {
    applySwap(world, p, fx, aim);
  } else if (fx.type === 'thorns') {
    applyThorns(world, p, owned, fx, effectCenter(p, fx.at, fx.maxRange, dir, aim, autoTarget));
  } else if (fx.type === 'summon') {
    applySummon(world, p, owned, fx, fx.at === 'aim' ? effectCenter(p, 'aim', fx.maxRange, dir, aim, autoTarget) : { x: p.pos.x + p.facing.x * SUMMON_SELF_OFFSET, y: p.pos.y + p.facing.y * SUMMON_SELF_OFFSET });
  } else {
    // tous les types d'effet sont traités : ajouter un type au schéma sans le câbler ici ne compile plus
    const unhandled: never = fx;
    void unhandled;
  }
}

/** Distance (px) devant le joueur où apparaît une invocation « sur soi ». */
const SUMMON_SELF_OFFSET = 40;

/** Invocation : `count` poupées au centre donné ; un identifiant inconnu est ignoré. */
function applySummon(world: World, p: Player, owned: OwnedSpell, fx: Extract<SpellEffect, { type: 'summon' }>, center: Vec2): void {
  const def = world.content.summons[fx.summonId];
  if (!def) return;
  for (let i = 0; i < fx.count; i++) {
    spawnSummon(world, def, center, p, owned.mods.damagePct);
    world.events.push({ type: 'summoned', summonId: def.id, pos: { ...center } });
  }
}

/** Trait Poison latent : les projectiles du sort principal empoisonnent même sans poison propre. */
function latentPoison(world: World, p: Player, owned: OwnedSpell): Poison | null {
  if (owned !== p.primary) return null;
  const trait = world.content.classes[p.defId]?.trait;
  return trait?.type === 'poison-latent' ? trait.poison : null;
}

/** Attirance : les monstres dans le rayon avancent vers le joueur (jusqu'au contact), dégâts optionnels. */
function applyPull(world: World, p: Player, owned: OwnedSpell, fx: Extract<SpellEffect, { type: 'pull' }>, center: Vec2): void {
  const radius = fx.radius * (1 + owned.mods.radiusPct / 100);
  const dmg = fx.damage ? scaleDamage(fx.damage, owned.mods.damagePct + playerDamagePct(world, p)) : null;
  for (const mon of entitiesOfKind<Monster>(world, 'monster')) {
    if (!mon.alive || Math.hypot(mon.pos.x - center.x, mon.pos.y - center.y) > radius + mon.radius) continue;
    const dx = p.pos.x - mon.pos.x, dy = p.pos.y - mon.pos.y;
    const dist = Math.hypot(dx, dy);
    const step = Math.min(fx.distance, dist - (mon.radius + p.radius));
    if (step > 0) mon.pos = clampToWalkable({ x: mon.pos.x + (dx / dist) * step, y: mon.pos.y + (dy / dist) * step }, mon.radius, world.room);
    if (dmg) hitTarget(world, p, mon, dmg, { lifestealPct: 0, slow: null, poison: null }, mon.pos);
  }
  world.events.push({ type: 'pull', pos: center, radius });
}

/** Transposition : le joueur échange sa place avec le monstre à portée le plus proche du point visé. */
function applySwap(world: World, p: Player, fx: Extract<SpellEffect, { type: 'swap' }>, aim: Vec2 | null): void {
  const point = aim ?? { x: p.pos.x + p.facing.x * fx.maxRange, y: p.pos.y + p.facing.y * fx.maxRange };
  let best: Monster | null = null;
  let bestDist = Infinity;
  for (const mon of entitiesOfKind<Monster>(world, 'monster')) {
    if (!mon.alive || Math.hypot(mon.pos.x - p.pos.x, mon.pos.y - p.pos.y) > fx.maxRange) continue;
    const d = Math.hypot(mon.pos.x - point.x, mon.pos.y - point.y);
    if (d < bestDist || (d === bestDist && best !== null && mon.id < best.id)) { best = mon; bestDist = d; }
  }
  if (!best) return;
  const from = { ...p.pos };
  const other = { ...best.pos };
  p.pos = clampToWalkable(other, p.radius, world.room);
  best.pos = clampToWalkable(from, best.radius, world.room);
  world.events.push({ type: 'blink', from, to: { ...p.pos } });
}

/** Ronces : zone persistante qui blesse (et ralentit, empoisonne) les monstres qui s'y trouvent ; le premier tic a lieu au prochain statusSystem. */
function applyThorns(world: World, p: Player, owned: OwnedSpell, fx: Extract<SpellEffect, { type: 'thorns' }>, center: Vec2): void {
  const radius = fx.radius * (1 + owned.mods.radiusPct / 100);
  const h = spawn<Hazard>(world, { kind: 'hazard', team: 'player', defId: owned.id, sprite: owned.id, pos: center, vel: { x: 0, y: 0 }, radius, facing: { x: 0, y: 0 }, hp: 1, hpMax: 1, alive: true, anim: 'idle', animTime: 0, until: world.time + fx.duration, nextTick: world.time, tick: { damage: scaleDamage(fx.tick.damage, owned.mods.damagePct), interval: fx.tick.interval }, slow: fx.slow ?? null, poison: fx.poison ?? null });
  world.events.push({ type: 'hazardPlaced', id: h.id, pos: { ...center }, radius, duration: fx.duration });
}

/** Coup en cône devant le joueur : portée et demi-angle grandissent avec radiusPct (angle plafonné à 180). */
function applyMelee(world: World, p: Player, owned: OwnedSpell, fx: Extract<SpellEffect, { type: 'melee' }>, dir: Vec2): void {
  const grow = 1 + owned.mods.radiusPct / 100;
  const range = fx.range * grow;
  const halfAngleDeg = Math.min(180, fx.halfAngleDeg * grow);
  const cosHalf = Math.cos(halfAngleDeg * Math.PI / 180);
  const dmg = scaleDamage(fx.damage, owned.mods.damagePct + playerDamagePct(world, p));
  for (const mon of entitiesOfKind<Monster>(world, 'monster')) {
    if (!mon.alive) continue;
    const c = bodyCenter(mon);
    const dx = c.x - p.pos.x, dy = c.y - p.pos.y;
    const dist = Math.hypot(dx, dy);
    if (dist > range + mon.radius) continue;
    if (dist >= 1 && (dir.x * dx + dir.y * dy) / dist < cosHalf) continue;
    hitTarget(world, p, mon, dmg, { lifestealPct: fx.lifestealPct, slow: fx.slow ?? null, poison: fx.poison ?? null }, mon.pos);
    if (fx.knockback > 0 && mon.alive) mon.pos = clampToWalkable({ x: mon.pos.x + dir.x * fx.knockback, y: mon.pos.y + dir.y * fx.knockback }, mon.radius, world.room);
  }
  world.events.push({ type: 'meleeHit', pos: { ...p.pos }, dir: { ...dir }, range, halfAngleDeg });
}

/** Bonus temporaire de statistiques et de dégâts ; relancer le même sort remplace le buff. */
function applyBuff(world: World, p: Player, owned: OwnedSpell, fx: Extract<SpellEffect, { type: 'buff' }>): void {
  const k = fx.scaleWithMissingHp ? Math.max(0.25, 1 - p.hp / p.hpMax) : 1;
  const stats: Partial<Stats> = {};
  for (const key of Object.keys(fx.stats) as (keyof Stats)[]) {
    const v = fx.stats[key];
    if (v !== undefined) stats[key] = Math.round(v * k);
  }
  p.buffs = p.buffs.filter((b) => b.id !== owned.id);
  p.buffs.push({ id: owned.id, until: world.time + fx.duration, stats, damagePct: fx.damagePct * k * (1 + owned.mods.damagePct / 100) });
  recomputeStats(world, p);
  world.events.push({ type: 'status', targetId: p.id, status: 'buff', duration: fx.duration });
}

export function spellSystem(world: World, input: InputFrame, dt: number): void {
  const p = getPlayer(world);
  if (!p) return;
  p.attackCooldown = Math.max(0, p.attackCooldown - dt);
  for (const k of Object.keys(p.cooldowns)) p.cooldowns[k] = Math.max(0, (p.cooldowns[k] ?? 0) - dt);
  if (!p.alive) return;
  const c = world.content;
  for (let i = 0; i < 4; i++) {
    const o = p.spells[i];
    if (!input.spells[i] || !o || (p.cooldowns[o.id] ?? 0) > 0) continue;
    const spell = c.spells[o.id];
    if (!spell) continue;
    p.cooldowns[o.id] = effectiveCooldown(spell, o, world.meta.cooldownPct);
    castSpell(world, p, o, input.aim);
    break;
  }
  if (input.primary && p.attackCooldown <= 0) {
    const spell = c.spells[p.primary.id];
    if (!spell) return;
    p.attackCooldown = effectiveCooldown(spell, p.primary, world.meta.cooldownPct);
    castSpell(world, p, p.primary, input.aim);
  }
}
